import { toggleClass } from "@cervval.js/client-html5-core/src/tools/css.tools";
import { createHtmlElement, REF_ATTRIBUTE } from "@cervval.js/client-html5-core/src/tools/dom.tools";
import { getLogService } from "@cervval.js/common/src/services/log.service";
import { anyReallyAny, getProp, setProp } from "@cervval.js/common/src/tools/any.tools";
import { toPx } from "@cervval.js/common/src/tools/number.tools";
import fastdom from "fastdom";
import { ColumnExcelLikeSortFilterConstructorOptions as ColumnExcelLikeSortFilterInitOptions } from "./columnExcelLikeSortFilter/columnExcelLikeSortFilter";
import { i18n } from "./i18n/i18n";
import { CjsSmartTable, HIGHLIGHT_TABLE_ROW_SELECTED, RenderCacheData, SmartTableCell, SmartTableOptions, TABLE_FOOTER_INFO_CELL, TABLE_FOOTER_INFO_DIV, TABLE_ROW_SELECTED } from "./smartTable";
import { columnWidthToString, getVisibleColumnsCount } from "./smartTableColumn.tools";

/** 
 * Bootstrap table styles available
 * @see https://getbootstrap.com/docs/5.0/content/tables
 * TODO: A merger dans un style commun "bootstrap" (et a utiliser pour table et pour les buttons)
 */
export enum SmartTableStyle {
    NONE = "none",
    PRIMARY = "primary",
    SECONDARY = "secondary",
    SUCCESS = "success",
    DANGER = "danger",
    WARNING = "warning",
    INFO = "info",
    LIGHT = "light",
    DARK = "dark",
    LINK = "link"
}


/** Mode of table selection */
export enum TableSelectorMode {
    SINGLE = "single",
    MULTIPLE = "multiple"
}

export interface SmartTableBounds {
    /** Timestamp of last render */
    uid: number;
    /** Bounds of table */
    table: DOMRect;
    /** Bounds of smartTable */
    smartTable: DOMRect;
    /** Bounds of body */
    body: DOMRect;
    /** Bounds of header */
    header: DOMRect;
    /** Bounds of footer */
    footer: DOMRect;
}

export interface SmartTableDisplayOptions<ItemData> {

    /** Style of table (primary, danger, info, etc.) */
    cssStyle: SmartTableStyle;

    /** Array of column uids to not filter */
    columnsToDisableFilter?: string[];

    /** Disable Filter */
    disableFilter?: boolean;

    /** Disable sort for the table */
    disableSort?: boolean;

    /** Disable title on columns for the table */
    disableTitle?: boolean;

    /** Always display "sort-none" icon in header */
    displaySortNone?: boolean;

    /** ExcelLike sort filter options */
    excelLikeSortFilter?: {

        /** SmartTable header click will open excel like component */
        enable: boolean,

        /** 
         * ExcelLikeSortFilter will be triggered on left or right click
         * must be set before init
         * @default "right"
         */
        clickInteraction?: "left" | "right",

        /** Prevent empty column list 
         * By default empty columns are not allowed and default columns are restored
         */
        preventEmptyColumns?: boolean,

        /**
         * ColumnExcelLikeSortFilterOptions
         */
        options?: Partial<ColumnExcelLikeSortFilterInitOptions<ItemData>>
    };

    /** Enable footer for table - not compatible with renderFooter */
    enableFooter?: boolean;

    /** Enable footer for table - not compatible with enableFooter */
    renderFooter?: (footer: HTMLElement, renderCacheData: RenderCacheData) => void;

    /** Display information in footer */
    enableFooterInfo?: {
        /** Display number of displayable rows information */
        displayableRowsCount?: boolean,

        /** Display number of selected rows information (nothing displayed if no selected rows) */
        selectedRowsCount?: boolean
    };

    /** Disable (hide) header of table */
    disableHeader?: boolean;

    /** Disable hover effect of table */
    disableHover?: boolean;

    /** If enabled, hover over a given cell will highlight all others col and row cells */
    enableCrossHover?: boolean;

    /** Disable stripped rows effect */
    disableStrippedRows?: boolean;

    /** Disable responsive table class */
    disableResponsiveTable?: boolean;

    /** Enable shadow on top/bottom when scrolling must be set before init */
    enableScrollShadows?: boolean;

    /** Options for table border */
    border?: {

        /** Add .table-bordered for borders on all sides of the table and cells. */
        bordered?: boolean,

        /** Add .table-borderless for a table without borders. */
        borderLess?: boolean,

        /** Border color can be added, based on Bootstrap color utilities */
        borderColor?: SmartTableStyle,

        /** 
         * css (top.borderWith + bottom.borderWith) height in px for each row
         * you need to set this value only if you have css that changes the border height
         * this property does not affect the border, it is used to compute the height taken by each row
         */
        rowCssBordersHeight?: number
    };

    /** 
     * Set table columns layout
     * auto: columns will take max width as possible, resize from each others
     * fixed: columns will all take equal size, can be updated with "setColumnWidth(*)"
     */
    columnsLayout: "auto" | "fixed";

    /** Array of column uids where sort is disabled*/
    columnsToDisableSort?: string[];

    /** Enable smartTable horizontal scroll. Table display is set to block. */
    enableHorizontalScroll?: boolean;

    /** 
     * This will add an empty column on the right that will fill the gap if columns don't fit the total width
     * This option is usually active with columnsLayout: "fixed", enableHorizontalScroll: true and resize enabled on columns
     * When setting these options, you must set a width on all columns
     */
    addFillColumn?: boolean;

    /**
     * If enabled, hovered cell will be highlighted (scale x1.4 effect)
     */
    highlightCellOnHover?: boolean;
}

export const defaultSmartTableDisplayOptions: SmartTableDisplayOptions<anyReallyAny> = {

    /** Style of table (primary, danger, info, etc.) */
    cssStyle: SmartTableStyle.NONE,

    /** 
     * Set table columns layout
     * auto: columns will take max width as possible, resize from each others
     * fixed: columns will all take equal size, can be updated with "setColumnWidth(*)"
     */
    columnsLayout: "fixed"
};

// Css classes
export const TABLE_HOVER = "table-hover";
export const TABLE_ROW_HOVER = "hovered-row";
const TABLE_STRIPED = "table-striped";
const TABLE_RESPONSIVE = "table-responsive";
const TABLE_BORDERLESS = "table-borderless";
const TABLE_BORDERED = "table-bordered";
const TABLE_LAYOUT_FIXED = "table-layout-fixed";
const TABLE_HIGHLIGHT_CELL_ON_HOVER = "highlight-cell-hover";
const TABLE_CROSS_HOVER_COLUMN = "cross-hover-column";
const TABLE_CROSS_HOVER_ROW = "cross-hover-row";

export class SmartTableStyleHelper<ItemData> {

    /** Get smartTable of renderer */
    public get table(): HTMLTableElement {
        return this._smartTable.table;
    }

    /** Get smartTable of renderer */
    public get options(): SmartTableOptions<ItemData> {
        return this._smartTable.options;
    }

    /** Store added css classes from options, use to clean button classes when refreshing component display */
    private _addedCssClassesFromOptions: string[] = [];

    /** Smart table i18n */
    private _i18n = i18n();

    /** Footer info div element */
    private _footerInfoDiv?: HTMLDivElement;

    constructor(private _smartTable: CjsSmartTable<ItemData>) {

        // Recompute margin left after render so info is always displayed
        this._smartTable.renderFinishEvent.on(() => {
            if (!this._footerInfoDiv) { return; }
            this._footerInfoDiv.style.marginLeft = toPx(this.getFooterInfoMarginLeftValue());
        });

    }

    /** Refresh table display */
    public refreshDisplayStyle(): void {

        // Clean added css classes
        this._addedCssClassesFromOptions.forEach(className => {
            this.table.classList.remove(className);
        });
        this._addedCssClassesFromOptions = [];

        // Apply css styling
        if (this.options.cssStyle && this.options.cssStyle !== SmartTableStyle.NONE) {
            const stylingClass = "table-" + this.options.cssStyle;
            this.table.classList.add(stylingClass);
            this._addedCssClassesFromOptions.push(stylingClass);
        }

        // Refresh border
        if (this.options.border) {
            const borderOptions = this.options.border;

            // If borderless is set, don't add others border styling
            if (borderOptions.borderLess) {
                this._smartTable.rowCssBordersHeight = 0;
                this.table.classList.add(TABLE_BORDERLESS);
                this._addedCssClassesFromOptions.push(TABLE_BORDERLESS);

            } else {
                this._smartTable.rowCssBordersHeight = borderOptions.rowCssBordersHeight ?? 1;

                // Add bordered class
                if (borderOptions.bordered) {
                    this.table.classList.add(TABLE_BORDERED);
                    this._addedCssClassesFromOptions.push(TABLE_BORDERED);
                }

                // Add border color class
                if (borderOptions.borderColor) {
                    const stylingClass = "border-" + borderOptions.borderColor;
                    this.table.classList.add(stylingClass);
                    this._addedCssClassesFromOptions.push(stylingClass);
                }
            }
        } else {
            this._smartTable.rowCssBordersHeight = 1; // default value (there is a border-bottom on td and th by default)
        }

        // Refresh hover
        toggleClass(this.table, !this.options.disableHover && !this.options.enableCrossHover, TABLE_HOVER);

        // Refresh stripped
        toggleClass(this.table, !this.options.disableStrippedRows, TABLE_STRIPED);

        // Refresh responsive table
        toggleClass(this.table, !this.options.disableResponsiveTable, TABLE_RESPONSIVE);

        // Refresh table layout
        toggleClass(this.table, this.options.columnsLayout !== "auto", TABLE_LAYOUT_FIXED);

        // Refresh highlight cell on hover effect
        toggleClass(this.table, this.options.highlightCellOnHover, TABLE_HIGHLIGHT_CELL_ON_HOVER);
    }

    /** 
     * Refresh display of selected rows 
     * table.refreshRowsSelectionFromSelectionModel is always called before refreshRowsSelectionDisplay
     */
    public refreshRowsSelectionDisplay(): void {

        for (const row of this._smartTable.displayedRows) {
            fastdom.mutate(() => {
                if (row.tr) {
                    toggleClass(row.tr, row.selected, TABLE_ROW_SELECTED);
                    toggleClass(row.tr, row.selected && Boolean(this._smartTable.options.selection?.enableBackgroundDisplay), HIGHLIGHT_TABLE_ROW_SELECTED);
                }
            });
        }

        for (const col of this._smartTable.options.columns) {
            fastdom.mutate(() => {
                col.refreshSelectionDisplay?.();
            });
        }
    }

    /**
     * Set a columnWith from a columnUid
     * @param columnUid must be a valid columnUid otherwise no action will be applied
     * @param {string} value accepts css values(px, em, rem, %, vw, vh)
     * 
     * ! For percentages table display should be "fixed" (options.columnsLayout === "fixed")
     * You need to render or refresh to apply changes
     */
    public setColumnWidth(columnUid: string, value: string | number, p?: { saveInConfig?: boolean }): void {
        const stringValue = columnWidthToString(value);
        if (!parseInt(stringValue, 10)) {
            getLogService().warn(`setColumnWidth: wrong value parameter format, waiting for a numeric value.(${value})`);
            return;
        }

        // Set width in column option
        const col = this._smartTable.options.columns.find(c => c.uid === columnUid);
        if (!col) { return; }// No column found for columnUid given
        col.width = stringValue;

        // Set header html style value
        if (col.headerHtml) {
            col.headerHtml.style.width = stringValue;
        }

        // Set width in column config
        const colConfig = this.options.columnsConfig?.find(cConfig => cConfig.uid === columnUid);
        if (!colConfig || p?.saveInConfig === false) { return; }// No column found for columnUid given
        colConfig.width = stringValue;
    }

    /**
     * Returns content to display in footer if enableFooterInfo option is activated
     * If displayableRowsCount option is true, display "15 rows" (no filter) or "8 of 15 rows" (if filtered)
     * If selectedRowsCount option is true, display "15 selected rows" or display nothing if no selected rows
     */
    public refreshFooterInfoDisplay(): void {
        if (!this._footerInfoDiv) { return; }

        const footerInfos: string[] = [];

        // Number of displayable rows
        if (this._smartTable.options.enableFooterInfo?.displayableRowsCount) {
            const nbrTotalRows = this._smartTable.tableRows.length;
            const nbrDisplayableRows = this._smartTable.displayableRows.length;
            footerInfos.push(nbrTotalRows === nbrDisplayableRows ? this._i18n.footerInfo.displayableRowsCount.noFilter[nbrTotalRows > 1 ? "plural" : "singular"]({ nbrTotalRows }) : this._i18n.footerInfo.displayableRowsCount.withFilter[nbrTotalRows > 1 ? "plural" : "singular"]({ nbrDisplayableRows, nbrTotalRows }));
        }

        // Number of selected rows
        if (this._smartTable.options.enableFooterInfo?.selectedRowsCount) {
            const nbrSelectedRows = this._smartTable.getSelectedRows().length;
            if (nbrSelectedRows) {
                footerInfos.push(this._i18n.footerInfo.selectedRowsCount[nbrSelectedRows > 1 ? "plural" : "singular"]({ nbrSelectedRows }));
            }
        }

        // Append content and set margin left to center the div
        this._footerInfoDiv.innerHTML = footerInfos.join(" - ");
        this._footerInfoDiv.style.marginLeft = toPx(this.getFooterInfoMarginLeftValue());
    }

    /** 
     * Get footer info left margin value
     * Compute value to always center the div even with horizontal scroll
     */
    public getFooterInfoMarginLeftValue(): number {
        if (!this._footerInfoDiv) { return 0; }
        return (this._smartTable.clientWidth / 2) + this._smartTable.components.smartTableContainer.scrollLeft - (this._footerInfoDiv.clientWidth / 2);
    }

    /** 
     * Get footer info content 
     */
    public getFooterInfoContent(): { tr: HTMLTableRowElement, td: HTMLTableCellElement, div: HTMLDivElement } {
        const div = createHtmlElement("div", {
            cssClass: [TABLE_FOOTER_INFO_DIV],
            attributes: {
                [REF_ATTRIBUTE]: "tableFooterInfoDiv"
            }
        });

        const td = createHtmlElement("td", {
            cssClass: [TABLE_FOOTER_INFO_CELL],
            attributes: {
                [REF_ATTRIBUTE]: "tableFooterInfoTd"
            },
            innerHTML: div
        });

        // Set colspan with cols.length value to take all space
        td.setAttribute("colspan", String(getVisibleColumnsCount(this.options.columns, this.options.columnsConfig, this.options.addFillColumn)));

        const tr = createHtmlElement("tr", {
            attributes: {
                [REF_ATTRIBUTE]: "tableFooterInfoTr"
            },
            innerHTML: td
        });
        this._footerInfoDiv = div;
        return { tr, td, div };
    }

    /** Install cross hover row/column hover highlight listeners if needed */
    public installCrossHover(): void {
        if (!this.options.enableCrossHover) { return; }

        const toggleCrossHoverClass = (cell: SmartTableCell<ItemData>, type: "add" | "remove"): void => {

            // Retrieve all cols/rows cells with cache
            const columnCells = this._smartTable.cellsByColumn[cell.column.uid];
            const rowCells = this._smartTable.cellsByRow[cell.row.uid];

            for (const crossCell of columnCells) {
                if (!crossCell.html) { continue; }
                // Toggle class
                toggleClass(crossCell.html, type === "add", TABLE_CROSS_HOVER_COLUMN);
            }

            for (const crossCell of rowCells) {
                if (!crossCell.html) { continue; }
                // Toggle class
                toggleClass(crossCell.html, type === "add", TABLE_CROSS_HOVER_ROW);
            }

            // Toggle class for header and footer
            const th = cell.column.headerHtml;
            if (th) { toggleClass(th, type === "add", TABLE_CROSS_HOVER_COLUMN); }

            const tf = cell.column.footerHtml;
            if (tf) { toggleClass(tf, type === "add", TABLE_CROSS_HOVER_COLUMN); }
        };

        for (const colCells of Object.values(this._smartTable.cellsByRowAndColumn)) {
            for (const cell of Object.values(colCells)) {
                const td = cell.html;

                // Ensure we don't register events twice
                if (!td || getProp(td, "cross-hover-installed")) { continue; }
                setProp(td, "cross-hover-installed", true);
                // toggle class on cell hover
                td.addEventListener("mouseenter", () => toggleCrossHoverClass(cell, "add"));
                td.addEventListener("mouseleave", () => toggleCrossHoverClass(cell, "remove"));
            }
        }
    }
}